import {
    Cell,
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    Header,
    PaginationState,
    Row,
    RowSelectionState,
    Table as TableType,
    useReactTable,
} from '@tanstack/react-table';
import React, {
    CSSProperties,
    ReactNode,
    SetStateAction,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../primitives/table';
import { cn } from '~/util';
import Load from './Load';
import { ClassNameValue } from 'tailwind-merge';
import { NotFoundComponent } from './DefaultNotFound';
import Search from '~/partials/Search';
import Paginator from './Paginator';
import { NavigateOptions } from '@tanstack/router-core';
import { Link } from '@tanstack/react-router';

interface DataTablePaginatorProps {
    pagination?: PaginationState;
    totalPages?: number;
    onPageChange?: React.Dispatch<SetStateAction<PaginationState>>;
    onTotalPagesComputed?: (n: number) => void;
    enableInternalPagination?: boolean;
}

interface DataTableSearchProps {
    search?: string;
    onSearchChange?: (value: string) => void;
    enableInternalFiltering?: boolean;
}

interface DataTableProps<TData, TValue>
    extends DataTablePaginatorProps, DataTableSearchProps {
    columns: ColumnDef<TData, TValue>[];
    data: TData[];
    idFn?: (data: TData) => string;
    onRowClick?: (data: {
        data: TData;
        row: Row<TData>;
        table: TableType<TData>;
    }) => void;
    onRowLink?: (data: {
        data: TData;
        row: Row<TData>;
        table: TableType<TData>;
    }) => NavigateOptions;
    multiselect?: boolean;
    selected?: TData[];
    noResultsFallback?: (afterCreate: (item: TData) => void) => ReactNode;
    isLoading?: boolean;
    rowClassName?: (row: Row<TData>) => ClassNameValue;
    renderMobileRow?: (row: Row<TData>) => ReactNode;
    headerClassName?: (header: Header<TData, unknown>) => ClassNameValue;
    cellClassName?: (cell: Cell<TData, TValue>) => ClassNameValue;
    tableClassName?: ClassNameValue;
    wrapperClassName?: ClassNameValue;
    cellStyle?: (cell: Cell<TData, TValue>) => CSSProperties;
    rowStyle?: (row: Row<TData>) => CSSProperties;
    disableBaseStyling?: boolean;
    disableHeaderOnMobile?: boolean;
    disableHeader?: boolean;
    styledHeader?: boolean;
    alwaysShowNoResultsFallback?: boolean;
}

function DataTable<TData, TValue>({
    columns,
    data,
    idFn,
    onRowClick,
    onRowLink,
    multiselect = false,
    selected,
    noResultsFallback,
    alwaysShowNoResultsFallback = false,
    isLoading,
    rowClassName,
    wrapperClassName,
    cellStyle,
    rowStyle,
    cellClassName,
    renderMobileRow,
    headerClassName,
    disableBaseStyling = false,
    disableHeader = false,
    disableHeaderOnMobile = false,
    styledHeader = false,
    tableClassName,
    pagination,
    onPageChange,
    onTotalPagesComputed,
    enableInternalPagination = false,
    search,
    onSearchChange,
    enableInternalFiltering = false,
}: DataTableProps<TData, TValue>) {
    const [internalRowSelection, setInternalRowSelection] =
        useState<RowSelectionState>({});

    const selectedRowSelection = useMemo(() => {
        if (!selected || !idFn) return {};
        return selected.reduce((acc: Record<string, boolean>, s) => {
            acc[idFn(s)] = true;

            return acc;
        }, {});
    }, [idFn, selected]);

    const rowSelection = selectedRowSelection ?? internalRowSelection;

    // eslint-disable-next-line react-hooks/incompatible-library
    const table = useReactTable({
        data,
        columns,
        manualPagination: !enableInternalPagination,
        enableGlobalFilter: enableInternalFiltering,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onRowSelectionChange: setInternalRowSelection,
        getRowId: idFn,
        enableMultiRowSelection: multiselect,
        state: {
            rowSelection,
            ...(enableInternalFiltering && search !== undefined
                ? { globalFilter: search }
                : {}),
            ...(enableInternalPagination && pagination
                ? {
                      pagination: {
                          pageIndex: pagination.pageIndex,
                          pageSize: pagination.pageSize,
                      },
                  }
                : {}),
        },
        onGlobalFilterChange: enableInternalFiltering
            ? undefined
            : onSearchChange,
        onPaginationChange: enableInternalPagination ? undefined : onPageChange,
    });

    useEffect(() => {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        if (!enableInternalPagination) return;

        const filteredCount = table.getFilteredRowModel().rows.length;
        const { pageSize } = table.getState().pagination;

        const total = Math.max(1, Math.ceil(filteredCount / pageSize));

        // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
        onTotalPagesComputed?.(total);

        const current =
            pagination?.pageIndex ?? table.getState().pagination.pageIndex;
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        if (current > total - 1) {
            // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
            onPageChange?.((prev) => ({
                ...prev,
                pageIndex: Math.max(0, total - 1),
            }));
        }
    }, [
        enableInternalPagination,
        onTotalPagesComputed,
        onPageChange,
        pagination?.pageIndex,
        pagination?.pageSize,
        search,
        data,
        table,
    ]);

    const rows = enableInternalPagination
        ? table.getPaginationRowModel().rows
        : table.getRowModel().rows;

    const renderRow = (row: Row<TData>) => {
        const rowLink = onRowLink?.({
            data: row.original,
            row,
            table,
        });

        return (
            <React.Fragment key={row.id}>
                <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    onClick={() =>
                        onRowClick?.({
                            data: row.original,
                            row,
                            table,
                        })
                    }
                    style={rowStyle?.(row)}
                    className={cn(
                        'group',
                        rowLink && 'relative cursor-pointer',
                        renderMobileRow && 'hidden lg:table-row',
                        !disableBaseStyling &&
                            (onRowClick || rowLink) &&
                            'cursor-pointer hover:bg-secondary transition-colors',
                        rowClassName?.(row),
                    )}
                >
                    {row.getVisibleCells().map((cell) =>
                        (() => {
                            const content = flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                            );

                            return (
                                <TableCell
                                    style={cellStyle?.(cell)}
                                    className={cn(
                                        rowLink && 'relative',
                                        cellClassName?.(cell),
                                        cell.getIsPlaceholder() &&
                                            'p-0 md:p-0 lg:p-0 xl:p-0 m-0 md:m-0 lg:m-0 xl:m-0',
                                    )}
                                    key={cell.id}
                                >
                                    <div className="relative z-1 pointer-events-none [&_a]:pointer-events-auto [&_button]:pointer-events-auto [&_input]:pointer-events-auto [&_select]:pointer-events-auto [&_textarea]:pointer-events-auto [&_div[role=button]]:pointer-events-auto">
                                        {content}
                                    </div>
                                    {rowLink && (
                                        <Link
                                            preload="intent"
                                            preloadDelay={200}
                                            {...rowLink}
                                            className="absolute inset-0 z-0 cursor-pointer"
                                            aria-label="Open row"
                                        />
                                    )}
                                </TableCell>
                            );
                        })(),
                    )}
                </TableRow>
                {renderMobileRow && (
                    <TableRow
                        key={`${row.id}-mobile`}
                        className="table-row lg:hidden"
                        data-state={row.getIsSelected() && 'selected'}
                        onClick={() =>
                            onRowClick?.({
                                data: row.original,
                                row,
                                table,
                            })
                        }
                    >
                        <TableCell
                            colSpan={columns.length}
                            className="relative p-0 m-0 border-0"
                        >
                            <div className="relative z-10 pointer-events-none [&_a]:pointer-events-auto [&_button]:pointer-events-auto [&_input]:pointer-events-auto [&_select]:pointer-events-auto [&_textarea]:pointer-events-auto [&_div[role=button]]:pointer-events-auto">
                                {renderMobileRow(row)}
                            </div>

                            {rowLink && (
                                <Link
                                    preload="intent"
                                    preloadDelay={200}
                                    {...rowLink}
                                    className="absolute inset-0 z-0 cursor-pointer"
                                    aria-label="Open row"
                                />
                            )}
                        </TableCell>
                    </TableRow>
                )}
            </React.Fragment>
        );
    };

    return (
        <div>
            <div
                className={cn(
                    !disableBaseStyling && 'rounded-lg border overflow-hidden',
                    wrapperClassName,
                )}
            >
                {isLoading ? (
                    <div className="flex min-h-[60vh] items-center justify-center">
                        <Load />
                    </div>
                ) : (
                    <>
                        <Table
                            className={cn(
                                disableBaseStyling
                                    ? 'border-separate border-spacing-y-2'
                                    : '',
                                tableClassName,
                            )}
                        >
                            {!disableHeader && (
                                <TableHeader
                                    className={cn(
                                        disableHeaderOnMobile &&
                                            'hidden lg:table-header-group',
                                    )}
                                >
                                    {table
                                        .getHeaderGroups()
                                        .map((headerGroup) => (
                                            <TableRow key={headerGroup.id}>
                                                {headerGroup.headers.map(
                                                    (header) => {
                                                        return (
                                                            <TableHead
                                                                key={header.id}
                                                                className={cn(
                                                                    styledHeader
                                                                        ? 'bg-primary text-primary-foreground'
                                                                        : '',
                                                                    headerClassName?.(
                                                                        header,
                                                                    ),
                                                                )}
                                                            >
                                                                {header.isPlaceholder
                                                                    ? null
                                                                    : flexRender(
                                                                          header
                                                                              .column
                                                                              .columnDef
                                                                              .header,
                                                                          header.getContext(),
                                                                      )}
                                                            </TableHead>
                                                        );
                                                    },
                                                )}
                                            </TableRow>
                                        ))}
                                </TableHeader>
                            )}

                            <TableBody>
                                {rows?.length ? (
                                    rows.map((row) => renderRow(row))
                                ) : (
                                    <TableRow className="hover:bg-inherit!">
                                        <TableCell
                                            colSpan={columns.length}
                                            className="h-24 text-center "
                                        >
                                            <NotFoundComponent customNotFound="No results" />
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </>
                )}
            </div>
            <div className="flex justify-center">
                {(alwaysShowNoResultsFallback && (search?.length ?? 0) > 0) ||
                rows?.length === 0
                    ? noResultsFallback?.((data) => {
                          const row = idFn
                              ? table.getRow(idFn(data))
                              : undefined;
                          if (row) {
                              onRowClick?.({
                                  data,
                                  row,
                                  table,
                              });
                          }
                      })
                    : null}
            </div>
        </div>
    );
}

export default DataTable;

interface DataTableWithControl<TData, TValue> extends Omit<
    DataTableProps<TData, TValue>,
    'noResultsFallback'
> {
    showPagination?: boolean;
    className?: string;
    noResultsFallback?: (
        search: string | undefined,
        afterCreate: (item: TData) => void,
    ) => ReactNode;
    alwaysShowNoResultsFallback?: boolean;
}

export function DataTableWithControl<TData, TValue>({
    search,
    pagination,
    totalPages,
    showPagination = true,
    onSearchChange,
    onPageChange,
    className,
    noResultsFallback,
    alwaysShowNoResultsFallback,
    ...dataTableProps
}: DataTableWithControl<TData, TValue>) {
    const [internalTotalPages, setInternalTotalPages] = useState<
        number | undefined
    >(undefined);

    const totalPagesToUse = dataTableProps.enableInternalPagination
        ? internalTotalPages
        : totalPages;

    return (
        <div className={cn('flex flex-col gap-2', className)}>
            <div className="flex flex-col gap-2 lg:gap-0 lg:flex-row lg:justify-between align-items">
                <div className="flex-1">
                    {search !== undefined && onSearchChange && (
                        <Search
                            initial_input={search}
                            onInputChange={(value: string) => {
                                onSearchChange(value);
                                onPageChange?.((prev) => ({
                                    ...prev,
                                    pageIndex: 0,
                                }));
                            }}
                        />
                    )}
                </div>
                <div className="flex items-center">
                    {showPagination &&
                        pagination != undefined &&
                        totalPagesToUse != undefined &&
                        onPageChange && (
                            <Paginator
                                currentPage={pagination.pageIndex + 1}
                                totalPages={totalPagesToUse ?? 1}
                                onPageChange={(value) =>
                                    onPageChange?.((prev) => ({
                                        ...prev,
                                        pageIndex: value - 1,
                                    }))
                                }
                            />
                        )}
                </div>
            </div>
            <DataTable
                search={search}
                pagination={pagination}
                onPageChange={onPageChange}
                onTotalPagesComputed={setInternalTotalPages}
                onSearchChange={onSearchChange}
                noResultsFallback={(afterCreate) =>
                    noResultsFallback?.(search, afterCreate)
                }
                alwaysShowNoResultsFallback={alwaysShowNoResultsFallback}
                {...dataTableProps}
            />
        </div>
    );
}
